import React from 'react';
import { Box, Card, CardContent, Typography, List, ListItem, ListItemIcon, ListItemText, useTheme, useMediaQuery, Button } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './TeaserCards.css';
import { motion } from 'framer-motion';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DownloadIcon from '@mui/icons-material/Download';

interface CardData {
  title: string;
  icon: React.ReactNode;
  content: string[];
}

const MotionCard = motion(Card);

const cardData: CardData[] = [
  {
    title: 'Product and Market Opportunity',
    icon: <StorefrontIcon />,
    content: [
      'NEXT BASKET is an all-in-one SaaS platform for creating and managing online stores and marketplaces, integrating software, logistics and artificial intelligence',
      'Unique advantages: AI core (built-in artificial intelligence at the core of the system); "5-in-1" integrated ecosystem (online store, ERP, logistics, marketing tools, support); global fulfillment network for warehousing and deliveries',
      'Huge addressable market: over 12 million online merchants globally; ~19% annual market growth for e-commerce platforms',
      'Target customers: small and medium-sized online retailers (SMB), large retail enterprises (Enterprise/B2B), manufacturers (D2C, direct-to-consumer) and traditional physical stores transitioning to online commerce',
      'NEXT BASKET has a complete product, sold in 7 countries on 4 continents'
    ]
  },
  {
    title: 'Traction and Business Model',
    icon: <TrendingUpIcon />,
    content: [
      'Strong traction: 38,000+ registered merchants (B2B users); 5,300+ active online stores; 4.5+ million end customers reached; 12-15% monthly sales growth through the platform',
      'Business models:\n1. SaaS subscription (monthly fee or % of turnover)\n2. First in the world D2C solutions:\n   2.1. DROPSHIPING model - sell fully finished, stocked products e-stores\n   2.2. GLOBAL model - for manufacturers from Asia to easily sell their products in Europe\n3. NB PRO - for the biggest online retailers',
      'Why it\'s scalable: built-in logistics infrastructure (warehouses in Europe integrated with DHL fulfillment, warehouse in Asia) and proprietary technology stack (built-in ERP, logistics, marketing - no external integrations)',
      'Platform is future-ready (Web3 architecture, crypto payments, token economy) - something competitors like Shopify/Magento are not prepared for'
    ]
  }
];

const TeaserCards = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = '/NEXTBASKET_Teaser.pdf';
      link.download = 'NEXTBASKET_Teaser.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Shared card rendering logic
  const renderCardContent = (card: CardData) => (
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box
          component="span"
          className="icon-wrapper"
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: 2,
            transition: 'transform 0.3s ease-in-out',
            '& > svg': {
              fontSize: { xs: 32, sm: 36, md: 40 },
              color: theme.palette.primary.main,
              transition: 'filter 0.3s ease-in-out'
            }
          }}
        >
          {card.icon}
        </Box>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            background: 'linear-gradient(135deg, #000 0%, #333 50%, #000 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' },
            letterSpacing: '0.02em',
            position: 'relative',
            zIndex: 5,
          }}
        >
          {card.title}
        </Typography>
      </Box>
      <List sx={{ py: 0 }}>
        {card.content.map((item, idx) => (
          <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                item.includes(':') ? (
                  <Box component="span">
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {item.split(':')[0]}:
                    </Box>
                    {item.split(':')[1]}
                  </Box>
                ) : (
                  item
                )
              }
              sx={{
                '& .MuiListItemText-primary': {
                  color: 'text.primary',
                  fontSize: { xs: '0.9rem', sm: '0.925rem', md: '0.95rem' },
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line',
                  position: 'relative',
                  zIndex: 5,
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </CardContent>
  );

  // Card styles
  const cardStyles = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    backgroundColor: 'rgba(63, 160, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(63, 160, 255, 0.15)',
    borderRadius: 2,
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
        linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
      `,
      backgroundSize: '60px 60px',
      opacity: 0.8,
      zIndex: 2
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '150%',
      height: '150%',
      background: 'radial-gradient(circle at center, rgba(63, 160, 255, 0.06) 0%, transparent 70%)',
      opacity: 0.7,
      zIndex: 1
    },
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 8px 30px rgba(63, 160, 255, 0.1)',
      border: '1px solid rgba(63, 160, 255, 0.25)',
      '& .icon-wrapper': {
        transform: 'scale(1.1)',
        '& svg': {
          filter: 'drop-shadow(0 0 8px rgba(63, 160, 255, 0.4))'
        }
      }
    },
  };

  return (
    <Box sx={{ py: { xs: 4, sm: 6, md: 8 }, overflowX: 'hidden' }}>
      <Typography
        variant="h3"
        component="h2"
        align="center"
        sx={{
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
          fontWeight: 700,
          mb: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 70%, #1a4b80 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textTransform: 'uppercase',
          '&::after': {
            content: '""',
            display: 'block',
            width: '60px',
            height: '4px',
            backgroundColor: theme.palette.primary.main,
            margin: '16px auto 0',
            borderRadius: '2px'
          }
        }}
      >
        Teaser
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: { xs: '0.85rem', sm: '0.9rem' },
          opacity: 0.8,
          mb: { xs: 3, sm: 4, md: 5 }
        }}
      >
        2 slides available
      </Typography>
      
      <Box sx={{ px: { xs: 0, md: 4 } }}>
        {isMobile ? (
          // Mobile view with Swiper
          <Swiper
            spaceBetween={20}
            slidesPerView={1}
            pagination={{ clickable: true }}
            navigation={true}
            modules={[Pagination, Navigation]}
            style={{ padding: '20px 10px 40px' }}
            wrapperClass="equal-height-slides"
          >
            {cardData.map((card, index) => (
              <SwiperSlide key={card.title}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  sx={cardStyles}
                >
                  {renderCardContent(card)}
                </MotionCard>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          // Desktop view with Grid
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '30px',
              padding: '20px 10px 40px'
            }}
          >
            {cardData.map((card, index) => (
              <MotionCard
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                sx={cardStyles}
              >
                {renderCardContent(card)}
              </MotionCard>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          sx={{
            py: { xs: 1.75, md: 1.75 },
            px: { xs: 4, md: 5 },
            minWidth: { xs: 'auto', md: '300px' },
            borderRadius: 15,
            textTransform: 'none',
            fontSize: { xs: '1.2rem', md: '1.1rem' },
            fontWeight: 600,
            letterSpacing: '0.02em',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: '#fff',
            border: '1px solid rgba(63, 160, 255, 0.3)',
            backdropFilter: 'blur(8px)',
            zIndex: theme.zIndex.modal + 1,
            pointerEvents: 'auto',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            '& .MuiSvgIcon-root': {
              color: '#fff',
              fontSize: '1.4rem',
              marginRight: '8px',
              transition: 'transform 0.3s ease'
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
              transform: 'translateX(-100%) skewX(-15deg)',
              transition: 'transform 0.7s ease'
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: '0 8px 25px rgba(63, 160, 255, 0.4)',
              transform: 'translateY(-2px)',
              border: '1px solid rgba(63, 160, 255, 0.5)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.1) rotate(-10deg)'
              },
              '&::before': {
                transform: 'translateX(100%) skewX(-15deg)'
              }
            },
            '&:active': {
              transform: 'translateY(1px)',
              boxShadow: '0 4px 15px rgba(63, 160, 255, 0.3)'
            }
          }}
        >
          Download PDF
        </Button>
      </Box>
    </Box>
  );
};

export default TeaserCards;
